import React, { useState } from "react";
import {
    Button,
    Icon,
    Input,
    message,
    Modal,
    Select,
    Table,
    Tooltip
} from "antd";
import style from "./index.module.scss";
import {
    ColumnProps,
    TableRowSelection,
    SortOrder,
    SorterResult
} from "antd/lib/table/interface";
import {
    copyExercise,
    CopyExerciseListItem,
    CourseInfoItem,
    getCopyExerciseList
} from "service/exercise";
import Moment from "moment";
import useRequestFn from "utils/useRequestFn";
import { useParams } from "react-router-dom";
import { errorMessage } from "service/service";
import { useUpdateEffect } from "react-use";
import { ResultMessage } from "components/ResultMessage";

interface ExerciseCloneModalProps {
    visible: boolean;
    onCancel: () => void;
    onOk: () => void;
    curQuestionNum: string;
    courseInfoList: CourseInfoItem[];
}

export default function ExerciseCloneModal({
    visible,
    onCancel,
    onOk,
    curQuestionNum,
    courseInfoList
}: ExerciseCloneModalProps) {
    const { Option } = Select;
    const { id } = useParams<{ id: string }>(); //当前练习活动id
    const [keyWord, setKeyWord] = useState(""); //输入的关键词
    const [courseSelected, setCourserSelected] = useState<string | undefined>(); //所选课程
    const [sortType, setSortType] = useState("2"); //排序规则:0：降序, 1：升序 ,2：默认
    const [pageSize, setPageSize] = useState<number>(10); //每页条数
    const [pageNum, setPageNum] = useState(1); //当前页
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[] | number[]>(
        []
    ); //当前选中项
    const sortDirections: SortOrder[] = ["descend", "ascend"];
    const [list, setList] = useState<CopyExerciseListItem[]>([]);
    const [noSearch, setNoSearch] = useState(true); //通过noSearch判断当前是不是未查询之前的状态，是则显示“请输入查询条件后查询”否则显示“无数据”

    const [getData, { loading: loadingData, data }] =
        useRequestFn(getCopyExerciseList);

    async function handleSearch() {
        try {
            await updateData();
            handleInitial();
        } catch (e) {
            message.error(errorMessage(e));
        }
    }

    async function updateData() {
        const params = {
            exerciseId: Number(id),
            search: keyWord,
            courseId: courseSelected || "",
            sortType,
            pageSize,
            currPage: pageNum
        };
        const data = await getData({ ...params });
        setList(data.list);
        setNoSearch(false);
    }

    //点击查询后初始化页面，创建时间显示倒序
    function handleInitial() {
        setPageNum(1);
        setSortType("0");
    }

    useUpdateEffect(() => {
        if (visible) updateData();
    }, [pageSize, pageNum, sortType]);

    const onTableChange = (
        pagination: any,
        filters: any,
        sorter: SorterResult<any>
    ) => {
        setSortType(
            sorter.order === "ascend"
                ? "1"
                : sorter.order === "descend"
                ? "0"
                : "2"
        );
        setPageNum(pagination.current);
        setPageSize(pagination.pageSize);
    };

    const columns: ColumnProps<CopyExerciseListItem>[] = [
        {
            title: "序号",
            dataIndex: "rowNo",
            align: "center",
            width: 70
        },
        {
            title: "练习名称",
            dataIndex: "exerciseName",
            align: "left",
            render: text => (
                <div className={style.column}>
                    <Tooltip placement="topLeft" title={text}>
                        {text}
                    </Tooltip>
                </div>
            )
        },
        {
            title: "课程信息",
            dataIndex: "courseNo",
            align: "left",
            render: (text, record) => (
                <div className={style.column}>
                    <Tooltip
                        placement="topLeft"
                        title={`[${text}-${record.courseSeq}]${record.courseName}`}>
                        [{text}-{record.courseSeq}]{record.courseName}
                    </Tooltip>
                </div>
            )
        },
        {
            title: "周次节信息",
            dataIndex: "weekTimeInfo",
            align: "left",
            render: text => (
                <div className={style.column}>
                    <Tooltip placement="topLeft" title={text}>
                        {text}
                    </Tooltip>
                </div>
            )
        },
        {
            title: "题数",
            dataIndex: "questionCount",
            align: "center",
            width: 80
        },
        {
            title: "创建时间",
            dataIndex: "createTime",
            align: "center",
            width: 160,
            sorter: true,
            sortDirections: sortDirections,
            sortOrder:
                sortType === "0"
                    ? "descend"
                    : sortType === "1"
                    ? "ascend"
                    : false,
            render: text => (
                <div>{Moment(text).format("YY/MM/DD HH:mm:ss")}</div>
            )
        }
    ];

    const pagination = {
        current: pageNum,
        total: list.length === 0 ? 0 : data?.allCount,
        pageSize: pageSize,
        pageSizeOptions: ["10", "20", "50", "100"],
        showSizeChanger: true,
        hideOnSinglePage: false,
        showQuickJumper: true,
        showTotal: (total: number) => <span>总计{total}条</span>
    };

    const rowSelection: TableRowSelection<CopyExerciseListItem> = {
        type: "radio",
        selectedRowKeys,
        onChange: (selectedRowKeys: string[] | number[]) => {
            setSelectedRowKeys(selectedRowKeys);
        }
    };

    async function addHandler() {
        try {
            const num = await copyExercise(id, selectedRowKeys?.join());
            curQuestionNum === String(num)
                ? message.warn("两个练习活动的题目相同，请勿重复添加")
                : ResultMessage.success("添加成功!");
            onOk();
            onCancel();
        } catch (e) {
            message.error(errorMessage(e));
        }
    }

    //关闭弹窗后初始化页面
    function onReset() {
        setList([]);
        setSelectedRowKeys([]);
        setSortType("2");
        setKeyWord("");
        setCourserSelected(undefined);
        setNoSearch(true);
        setPageSize(10);
    }

    const footer = (
        <div>
            <Button type="default" onClick={onCancel}>
                取消
            </Button>
            <Button
                type="primary"
                onClick={addHandler}
                disabled={selectedRowKeys.length === 0}>
                确认
            </Button>
        </div>
    );

    return (
        <Modal
            title="复制练习"
            width={1200}
            visible={visible}
            className={style.root}
            footer={footer}
            onCancel={onCancel}
            onOk={addHandler}
            maskClosable={false}
            afterClose={onReset}>
            <div className={style.header}>
                <div className={style.left}>
                    <div className={style.leftItem}>
                        <span className={style.subTitle}>课程信息</span>
                        <Select
                            placeholder="请选择课程"
                            onChange={(e: any) => setCourserSelected(e)}
                            className={style.multipleCheck}
                            value={courseSelected}>
                            {courseInfoList &&
                                courseInfoList.map(item => (
                                    <Option key={item.id}>
                                        {item.courseNo}
                                        {item.courseSeq}
                                        {item.courseName}
                                    </Option>
                                ))}
                        </Select>
                    </div>
                </div>

                <div className={style.right}>
                    <Input
                        className={style.input}
                        placeholder="请输入练习活动名称关键词"
                        value={keyWord}
                        onChange={e => setKeyWord(e.target.value)}
                    />
                    <Button
                        type="primary"
                        className={style.button}
                        onClick={handleSearch}>
                        <Icon type="search" /> 查询
                    </Button>
                </div>
            </div>

            <div className={style.body}>
                <Table
                    columns={columns}
                    rowKey="exerciseId"
                    dataSource={list}
                    pagination={pagination}
                    rowSelection={rowSelection}
                    scroll={{ y: 412 }}
                    loading={loadingData}
                    onChange={onTableChange}
                    locale={{
                        emptyText: (
                            <div className={style.empty}>
                                {noSearch ? "请输入查询条件后查询" : "无数据"}
                            </div>
                        )
                    }}
                />
            </div>
        </Modal>
    );
}
